/**
 * Composite Query Hook for CD Data
 *
 * Orchestrates parallel fetching of all CD-related data and initializes
 * the Zustand store. Supports create, edit, and preview modes.
 *
 * Key behaviors:
 * - Fetches transaction, loanTerms, parties, lineItems, calculations in parallel
 * - Always uses live data from individual tables (single source of truth)
 * - Initializes Zustand store when all data is ready
 *
 * Note: Snapshots/versions are for archival only and not used for hydration.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { closingDisclosureApi } from '../services/closingDisclosureApi';
import { useClosingDisclosureStore } from '../store/closingDisclosureStore';
import type { CalculatedTotals, LineItem, LoanTerms, Party, Transaction } from '../types';
import type { ServerData } from '../utils/transformData';
import { cdQueryKeys } from './useClosingDisclosureQueries';

/**
 * Operation mode for CD data loading
 */
export type CDMode = 'create' | 'edit' | 'preview';

/**
 * Options for the useCDData hook
 */
export interface UseCDDataOptions {
  /**
   * Transaction ID to load data for (undefined for create mode)
   */
  transactionId: string | undefined;

  /**
   * Operation mode
   * - create: No data fetching, starts with empty form
   * - edit: Fetches live data from individual tables
   * - preview: Same as edit (snapshots are for archive only)
   */
  mode: CDMode;

  /**
   * Whether queries should be enabled (default: true)
   */
  enabled?: boolean;

  /**
   * Whether to automatically initialize the store when data is loaded (default: true)
   */
  autoInitialize?: boolean;
}

/**
 * Return type for useCDData hook
 */
export interface UseCDDataResult {
  /**
   * Combined loading state for all queries
   */
  isLoading: boolean;

  /**
   * Whether any query has an error
   */
  isError: boolean;

  /**
   * Combined error message
   */
  error: Error | null;

  /**
   * Whether all data has been successfully loaded
   */
  isReady: boolean;

  /**
   * Individual query results for fine-grained access
   */
  queries: {
    transaction: ReturnType<typeof useQuery<Transaction, Error>>;
    loanTerms: ReturnType<typeof useQuery<LoanTerms | null, Error>>;
    parties: ReturnType<typeof useQuery<Party[], Error>>;
    lineItems: ReturnType<typeof useQuery<LineItem[], Error>>;
    calculations: ReturnType<typeof useQuery<CalculatedTotals | null, Error>>;
  };

  /**
   * Refetch all queries
   */
  refetchAll: () => Promise<void>;

  /**
   * Manually initialize the store with current data
   */
  initializeStore: () => void;
}

/**
 * Composite query hook for loading CD data
 *
 * Orchestrates all CD-related queries and hydrates the Zustand store.
 * Always uses live data - snapshots are for archive/audit only.
 *
 * @example
 * ```tsx
 * // Preview mode - fetches live data
 * const { isLoading, isReady, queries } = useCDData({
 *   transactionId: 'abc-123',
 *   mode: 'preview',
 * });
 *
 * // Edit mode - same as preview
 * const { isLoading, initializeStore } = useCDData({
 *   transactionId: 'abc-123',
 *   mode: 'edit',
 * });
 *
 * // Create mode - no fetching, empty form
 * const { isReady } = useCDData({
 *   transactionId: undefined,
 *   mode: 'create',
 * });
 * ```
 */
export function useCDData(options: UseCDDataOptions): UseCDDataResult {
  const { transactionId, mode, enabled = true, autoInitialize = true } = options;

  const queryClient = useQueryClient();

  // Store access
  const store = useClosingDisclosureStore();

  // Track if we've already initialized to prevent duplicate initializations
  const hasInitializedRef = useRef(false);
  const lastTransactionIdRef = useRef<string | undefined>(undefined);

  // Reset initialization tracking and mark loading when transaction ID changes
  // This prevents showing stale data from a previous transaction while loading
  if (transactionId !== lastTransactionIdRef.current) {
    hasInitializedRef.current = false;
    lastTransactionIdRef.current = transactionId;
    // Mark store as loading to prevent stale UI
    if (transactionId && mode !== 'create') {
      store.setLoadingState('loading');
    }
  }

  // Determine if queries should run
  const shouldFetch = enabled && !!transactionId && mode !== 'create';

  // ============================================================================
  // Individual Queries
  // ============================================================================

  // Transaction query
  const transactionQuery = useQuery({
    queryKey: cdQueryKeys.transaction(transactionId!),
    queryFn: async () => {
      const response = await closingDisclosureApi.getById(transactionId!);
      return response.data;
    },
    enabled: shouldFetch,
    staleTime: 30000, // 30 seconds
  });

  // Loan terms query
  const loanTermsQuery = useQuery<LoanTerms | null, Error>({
    queryKey: cdQueryKeys.loanTerms(transactionId!),
    queryFn: async () => {
      try {
        const response = await closingDisclosureApi.getLoanTerms(transactionId!);
        return response.data;
      } catch {
        // Loan terms might not exist yet
        return null;
      }
    },
    enabled: shouldFetch,
    staleTime: 30000,
  });

  // Parties query
  const partiesQuery = useQuery<Party[], Error>({
    queryKey: cdQueryKeys.parties(transactionId!),
    queryFn: async () => {
      const response = await closingDisclosureApi.getParties(transactionId!);
      return response.data;
    },
    enabled: shouldFetch,
    staleTime: 30000,
  });

  // Line items query
  const lineItemsQuery = useQuery<LineItem[], Error>({
    queryKey: cdQueryKeys.lineItems(transactionId!),
    queryFn: async () => {
      const response = await closingDisclosureApi.getLineItems(transactionId!);
      return response.data;
    },
    enabled: shouldFetch,
    staleTime: 30000,
  });

  // Calculations query
  const calculationsQuery = useQuery<CalculatedTotals | null, Error>({
    queryKey: cdQueryKeys.calculations(transactionId!),
    queryFn: async () => {
      try {
        const response = await closingDisclosureApi.getCalculations(transactionId!);
        return response.data;
      } catch {
        // Calculations might not be available
        return null;
      }
    },
    enabled: shouldFetch,
    staleTime: 30000,
  });

  // ============================================================================
  // Derived State
  // ============================================================================

  // Combined loading state
  const isLoading = useMemo(() => {
    if (mode === 'create') return false;
    if (!shouldFetch) return false;

    return (
      transactionQuery.isLoading ||
      loanTermsQuery.isLoading ||
      partiesQuery.isLoading ||
      lineItemsQuery.isLoading ||
      calculationsQuery.isLoading
    );
  }, [
    mode,
    shouldFetch,
    transactionQuery.isLoading,
    loanTermsQuery.isLoading,
    partiesQuery.isLoading,
    lineItemsQuery.isLoading,
    calculationsQuery.isLoading,
  ]);

  // Combined error state
  const isError = useMemo(() => {
    if (mode === 'create') return false;
    if (!shouldFetch) return false;

    // Transaction error is always critical
    return (
      transactionQuery.isError ||
      loanTermsQuery.isError ||
      partiesQuery.isError ||
      lineItemsQuery.isError ||
      calculationsQuery.isError
    );
  }, [
    mode,
    shouldFetch,
    transactionQuery.isError,
    loanTermsQuery.isError,
    partiesQuery.isError,
    lineItemsQuery.isError,
    calculationsQuery.isError,
  ]);

  // Combined error
  const error = useMemo(() => {
    if (transactionQuery.error) return transactionQuery.error;
    if (loanTermsQuery.error) return loanTermsQuery.error;
    if (partiesQuery.error) return partiesQuery.error;
    if (lineItemsQuery.error) return lineItemsQuery.error;
    if (calculationsQuery.error) return calculationsQuery.error;
    return null;
  }, [
    transactionQuery.error,
    loanTermsQuery.error,
    partiesQuery.error,
    lineItemsQuery.error,
    calculationsQuery.error,
  ]);

  // Ready state
  const isReady = useMemo(() => {
    if (mode === 'create') return true;
    if (!shouldFetch) return false;
    if (isLoading || isError) return false;

    // Need transaction data at minimum
    return transactionQuery.isSuccess;
  }, [mode, shouldFetch, isLoading, isError, transactionQuery.isSuccess]);

  // ============================================================================
  // Store Initialization
  // ============================================================================

  /**
   * Manually initialize the store with current data
   */
  const initializeStore = useCallback(() => {
    if (!transactionId) {
      store.clearStore();
      return;
    }

    // Skip if already initialized for this transaction
    if (hasInitializedRef.current) {
      return;
    }

    try {
      if (transactionQuery.data) {
        const serverData: ServerData = {
          transaction: transactionQuery.data,
          loanTerms: loanTermsQuery.data || null,
          parties: partiesQuery.data || [],
          lineItems: lineItemsQuery.data || [],
          calculations: calculationsQuery.data || null,
        };
        store.hydrateFromServer(serverData);
      }

      hasInitializedRef.current = true;
    } catch (err) {
      store.setLoadingState('error', err instanceof Error ? err.message : 'Failed to load data');
    }
  }, [
    transactionId,
    transactionQuery.data,
    loanTermsQuery.data,
    partiesQuery.data,
    lineItemsQuery.data,
    calculationsQuery.data,
    store,
  ]);

  // Auto-initialize store when data is ready
  useEffect(() => {
    if (!autoInitialize) return;
    if (mode === 'create') return;
    if (!isReady) return;
    if (hasInitializedRef.current) return;

    initializeStore();
  }, [autoInitialize, mode, isReady, initializeStore]);

  // ============================================================================
  // Actions
  // ============================================================================

  /**
   * Refetch all queries
   */
  const refetchAll = async () => {
    hasInitializedRef.current = false;

    if (!transactionId) return;

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.transaction(transactionId) }),
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.loanTerms(transactionId) }),
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.parties(transactionId) }),
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.lineItems(transactionId) }),
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.calculations(transactionId) }),
    ]);
  };

  // ============================================================================
  // Return
  // ============================================================================

  return {
    isLoading,
    isError,
    error,
    isReady,
    queries: {
      transaction: transactionQuery,
      loanTerms: loanTermsQuery,
      parties: partiesQuery,
      lineItems: lineItemsQuery,
      calculations: calculationsQuery,
    },
    refetchAll,
    initializeStore,
  };
}

export default useCDData;
